import CostElement from "../models/CostElement";
import WorkCenter from '../models/WorkCenter';
import RateDefault from '../models/RateDefault';
import Factory from '../models/Factory';
import FormulaOption from '../models/FormulaOption';
import StyleCostConfiguration from '../models/StyleCostConfiguration';
import SAPMaterialGroup from '../models/SAPMaterialGroup';
import StyleMinute from '../models/StyleMinute';
import LazyState from '../models/LazyState';
import { DataTableFilterEvent, DataTablePageEvent } from 'primereact/datatable';
import RateDefaultList from '../models/RateDefaultList';
import apiClient from '../components/apiClient';

export class BomCostingService {
    private static formatDate(dateTimeString: string): string {
        const date = new Date(dateTimeString);
        return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
    }

    static getAllWorkCenters(): Promise<WorkCenter[]> {
        return apiClient.get<WorkCenter[]>(`/Costing/GetAllWorkCenters`).then(res => res.data);
    }

    static addWorkCenter(workCenter: WorkCenter): Promise<WorkCenter> {
        return apiClient.post<WorkCenter>(`/Costing/AddWorkCenter`, workCenter).then(res => res.data);
    }

    static updateWorkCenter(workCenter: WorkCenter): Promise<WorkCenter> {
        return apiClient.put<WorkCenter>(`/Costing/UpdateWorkCenter`, workCenter).then(res => res.data);
    }

    static deleteWorkCenter(id: number): Promise<boolean> {
        return apiClient.delete<boolean>(`/Costing/DeleteWorkCenter/${id}`).then(res => res.data);
    }

    static getAllCostElements(): Promise<CostElement[]> {
        return apiClient.get<CostElement[]>(`/Costing/GetAllCostElements`).then(res => res.data);
    }

    static addCostElement(costElement: CostElement): Promise<CostElement> {
        return apiClient.post<CostElement>(`/Costing/AddCostElement`, costElement).then(res => res.data);
    }

    static updateCostElement(costElement: CostElement): Promise<CostElement> {
        return apiClient.put<CostElement>(`/Costing/UpdateCostElement`, costElement).then(res => res.data);
    }

    static deleteCostElement(id: number): Promise<boolean> {
        return apiClient.delete<boolean>(`/Costing/DeleteCostElement/${id}`).then(res => res.data);
    }

    static getAllRateDefaults(): Promise<RateDefault[]> {
        return apiClient.get<RateDefault[]>(`/Costing/GetAllRateDefaults`)
            .then(res => res.data)
            .then(rateDefaults => {
                return rateDefaults.map(rd => ({
                    ...rd,
                    effectiveDate: rd.effectiveDate ? this.formatDate(rd.effectiveDate) : '',
                    expirationDate: rd.expirationDate ? this.formatDate(rd.expirationDate) : ''
                }));
            });
    }

    static getAllRateDefaultsLazy(lazyState: LazyState | DataTablePageEvent | DataTableFilterEvent): Promise<RateDefaultList> {
        return apiClient.post<RateDefaultList>(`/Costing/GetAllRateDefaultsLazy`, lazyState)
            .then(res => res.data)
            .then(rateDefaults => {
                rateDefaults.rateDefaults = rateDefaults.rateDefaults.map(rd => ({
                    ...rd,
                    effectiveDate: rd.effectiveDate ? this.formatDate(rd.effectiveDate) : '',
                    expirationDate: rd.expirationDate ? this.formatDate(rd.expirationDate) : ''
                }));
                return rateDefaults
            });
    }

    static addRateDefault(rateDefault: RateDefault): Promise<RateDefault> {
        return apiClient.post<RateDefault>(`/Costing/AddRateDefault`, rateDefault).then(res => res.data);
    }

    static updateRateDefault(rateDefault: RateDefault): Promise<RateDefault> {
        return apiClient.put<RateDefault>(`/Costing/UpdateRateDefault`, rateDefault).then(res => res.data);
    }

    static deleteRateDefault(id: number): Promise<boolean> {
        return apiClient.delete<boolean>(`/Costing/DeleteRateDefault/${id}`).then(res => res.data);
    }

    static getAllFactories(): Promise<Factory[]> {
        return apiClient.get<Factory[]>(`/Costing/GetAllFactories`).then(res => res.data);
    }

    static getAllFormulaOptions(): Promise<FormulaOption[]> {
        return apiClient.get<FormulaOption[]>(`/Costing/GetAllFormulaOptions`).then(res => res.data);
    }

    static getAllStyleCostConfigurationsByStyle(id: string): Promise<StyleCostConfiguration[]> {
        return apiClient.get<StyleCostConfiguration[]>(`/Costing/GetAllStyleCostConfigurationsByStyle/${id}`)
            .then(res => res.data)
            .then(styleCostConfigurations => {
                return styleCostConfigurations.map(rd => ({
                    ...rd,
                    effectiveDate: rd.effectiveDate ? this.formatDate(rd.effectiveDate) : '',
                    expirationDate: rd.expirationDate ? this.formatDate(rd.expirationDate) : ''
                }));
            });
    }

    static addStyleCostConfiguration(styleCostConfiguration: StyleCostConfiguration): Promise<StyleCostConfiguration> {
        return apiClient.post<StyleCostConfiguration>(`/Costing/AddStyleCostConfiguration`, styleCostConfiguration).then(res => res.data);
    }

    static updateStyleCostConfiguration(styleCostConfiguration: StyleCostConfiguration): Promise<StyleCostConfiguration> {
        return apiClient.put<StyleCostConfiguration>(`/Costing/UpdateStyleCostConfiguration`, styleCostConfiguration).then(res => res.data);
    }

    static deleteStyleCostConfiguration(id: number): Promise<boolean> {
        return apiClient.delete<boolean>(`/Costing/DeleteStyleCostConfiguration/${id}`).then(res => res.data);
    }

    static getAllSAPMaterialGroup(): Promise<SAPMaterialGroup[]> {
        return apiClient.get<SAPMaterialGroup[]>(`/Costing/GetAllSAPMaterialGroup`).then(res => res.data);
    }

    static getMinutesByStyleCode(id: string): Promise<StyleMinute> {
        return apiClient.get<StyleMinute>(`/Costing/GetMinutesByStyleCode/${id}`).then(res => {
            return res.data;
        });
    }

    static updateMinutesByStyleCode(styleMinute: StyleMinute): Promise<StyleMinute> {
        return apiClient.put<StyleMinute>(`/Costing/UpdateMinutesByStyleCode`, styleMinute).then(res => res.data);
    }

    static getRateDefaultByMaterialGroup(materialGroup: string): Promise<RateDefault[]> {
        return apiClient.get<RateDefault[]>(`/Costing/GetRateDefaultByMaterialGroup/${materialGroup}`)
            .then(res => res.data)
            .then(rateDefaults => {
                return rateDefaults.map(rd => ({
                    ...rd,
                    effectiveDate: rd.effectiveDate ? this.formatDate(rd.effectiveDate) : '',
                    expirationDate: rd.expirationDate ? this.formatDate(rd.expirationDate) : ''
                }));
            });
    }
}